import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Search, MapPin, X } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function TopNav() {
  const { cartCount, cartTotal } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // Search input is driven by the URL so it stays in sync with whatever filter
  // is currently active. Empties out when user picks a category.
  const urlSearch = new URLSearchParams(location.search).get('search') || '';
  const [searchQuery, setSearchQuery] = useState(urlSearch);

  useEffect(() => {
    setSearchQuery(urlSearch);
  }, [urlSearch, location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`/products?search=${encodeURIComponent(q)}`);
    } else {
      navigate('/products');
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    if (urlSearch) navigate('/products');
  };

  return (
    <header className="sticky top-0 bg-white border-b border-border z-40" data-testid="top-nav">
      <div className="bg-primary text-primary-foreground py-1.5 px-4 text-center text-sm hidden md:block">
        <span className="flex items-center justify-center gap-2">
          <MapPin className="w-4 h-4" />
          Delivering in 30-45 mins across your town
        </span>
      </div>

      <div className="px-4 py-3 max-w-7xl mx-auto">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <h1 className="text-xl md:text-2xl font-extrabold text-primary tracking-tight">
              QUICK GRAM <span className="text-accent">TURA</span>
            </h1>
          </Link>

          {/* Search - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for groceries, vegetables, fruits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 h-11 rounded-full bg-muted/50 border-none"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="search-clear-desktop"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/products" className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-products">
              All Products
            </Link>
            <Link to="/my-orders" className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-my-orders">
              My Orders
            </Link>
            <Link to="/admin" className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-admin">
              Admin
            </Link>
          </nav>

          <Link to="/cart" data-testid="cart-button">
            <Button variant="outline" className="relative rounded-full h-11 px-4 gap-2 border-primary/20">
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline font-medium">
                ₹{cartTotal.toFixed(0)}
              </span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Button>
          </Link>
        </div>

        {/* Search - Mobile */}
        <form onSubmit={handleSearch} className="mt-3 md:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search groceries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 h-11 rounded-full bg-muted/50 border-none"
              data-testid="search-input-mobile"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="search-clear-mobile"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>
      </div>
    </header>
  );
}
